import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { CreatePurchaseOrderDto, PurchaseOrder } from '../../services/purchase.service';
import { inventoryService } from '../../services/inventory.service';
import {
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useAmendPurchaseOrder,
  useActiveVendors,
} from '../../hooks/usePurchase';
import { Modal } from '../../components/ui/Modal';
import {
  Field,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { useActiveGstRates } from '../../hooks/useGstRates';
import { PlantField, plantPayload, usePlantChoice } from '../../components/common/PlantField';

interface PurchaseOrderItemFormData {
  productId: string;
  quantity: number; // valueAsNumber converts to number
  unitPrice: number; // valueAsNumber converts to number
  taxRate: number; // valueAsNumber converts to number
}

interface PurchaseOrderFormData {
  vendorId: string;
  expectedDate: string; // Form uses string for date input
  items: PurchaseOrderItemFormData[];
  notes?: string;
  // Why an approved order is changed; asked only when amending
  reason?: string;
}

interface CreatePurchaseOrderProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // When set, the dialog edits this draft order instead of creating a new one
  order?: PurchaseOrder | null;
}

// A date in YYYY-MM-DD for date inputs, in the user's timezone
const toDateInput = (value: string | Date) => {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayInputValue = () => toDateInput(new Date());

const newItem = (taxRate: number): PurchaseOrderItemFormData => ({
  productId: '',
  quantity: 1,
  unitPrice: 0,
  taxRate,
});

const getDefaultValues = (order: PurchaseOrder | null | undefined, defaultTaxRate: number): PurchaseOrderFormData =>
  order
    ? {
        vendorId: order.vendorId || order.vendor?.id || '',
        expectedDate: toDateInput(order.expectedDate),
        notes: order.notes || '',
        items:
          order.items?.length > 0
            ? order.items.map((item) => ({
                productId: item.productId,
                quantity: Number(item.quantity),
                unitPrice: Number(item.unitPrice),
                taxRate: Number(item.taxRate),
              }))
            : [newItem(defaultTaxRate)],
      }
    : {
        vendorId: '',
        expectedDate: todayInputValue(),
        items: [newItem(defaultTaxRate)],
        notes: '',
      };

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, title, description, action, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
          {step}
        </span>
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const CreatePurchaseOrder: React.FC<CreatePurchaseOrderProps> = ({
  isOpen,
  onClose,
  onSuccess,
  order,
}) => {
  const isEdit = !!order;
  const plant = usePlantChoice();
  const [plantAsked, setPlantAsked] = useState(false);
  // An order that is already approved is amended, not edited: the change is recorded with a reason
  const isAmend = !!order && order.status !== 'draft';
  // Tax rates come from the GST rate master
  const { optionsFor, defaultRate } = useActiveGstRates();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<PurchaseOrderFormData>({
    defaultValues: getDefaultValues(order, defaultRate),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = useWatch({ control, name: 'items' });
  const watchedVendorId = useWatch({ control, name: 'vendorId' });

  // Only load the dropdown data while the form is open
  const { data: vendors = [], isLoading: vendorsLoading, error: vendorsError } = useActiveVendors(isOpen);
  const { data: products = [], isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: () => inventoryService.getProducts(),
    enabled: isOpen,
  });

  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  const amendMutation = useAmendPurchaseOrder();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Start from the right values every time the dialog opens
  useEffect(() => {
    if (isOpen) {
      reset(getDefaultValues(order, defaultRate));
    }
    // defaultRate is here so a form opened before the rates loaded picks up the default
  }, [isOpen, order, reset, defaultRate]);

  const onSubmit = async (data: PurchaseOrderFormData) => {
    setPlantAsked(true);
    if (!isEdit && plant.missing) return;
    const payload: CreatePurchaseOrderDto = {
      vendorId: data.vendorId,
      ...(isEdit ? {} : plantPayload(plant.plantId)),
      expectedDate: data.expectedDate,
      notes: data.notes?.trim() || (isEdit ? '' : undefined),
      items: data.items.map((item) => ({
        productId: item.productId,
        quantity: toNumber(item.quantity),
        unitPrice: toNumber(item.unitPrice),
        taxRate: toNumber(item.taxRate),
      })),
    };

    try {
      if (order && isAmend) {
        const { vendorId: _vendor, ...changes } = payload;
        const amended = await amendMutation.mutateAsync({ id: order.id, data: { ...changes, reason: (data.reason ?? '').trim() } });
        toast.success(`${amended.orderNumber} amended (revision ${amended.revision})`);
      } else if (order) {
        await updateMutation.mutateAsync({ id: order.id, data: payload });
        toast.success('Purchase order updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Purchase order created successfully!');
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, isAmend ? 'The purchase order could not be amended' : isEdit ? 'Failed to update purchase order' : 'Failed to create purchase order'),
      );
    }
  };

  const lineTotals = (watchedItems || []).map((item) => {
    const subtotal = toNumber(item?.quantity) * toNumber(item?.unitPrice);
    const tax = (subtotal * toNumber(item?.taxRate)) / 100;
    return { subtotal, tax, total: subtotal + tax };
  });
  const subtotal = lineTotals.reduce((sum, line) => sum + line.subtotal, 0);
  const taxTotal = lineTotals.reduce((sum, line) => sum + line.tax, 0);
  const grandTotal = subtotal + taxTotal;
  const totalUnits = (watchedItems || []).reduce(
    (sum, item) => sum + (item?.productId ? toNumber(item.quantity) : 0),
    0,
  );
  const itemCount = (watchedItems || []).filter((item) => item?.productId).length;

  // When editing, the order's vendor stays selectable even if it has since been deactivated
  const vendorOptions =
    order?.vendor && !vendors.some((vendor) => vendor.id === order.vendor.id)
      ? [order.vendor, ...vendors]
      : vendors;
  const selectedVendor = vendorOptions.find((vendor) => vendor.id === watchedVendorId);

  const errorCount =
    (errors.vendorId ? 1 : 0) +
    (errors.expectedDate ? 1 : 0) +
    (errors.notes ? 1 : 0) +
    (Array.isArray(errors.items)
      ? errors.items.reduce((sum, item) => sum + (item ? Object.keys(item).length : 0), 0)
      : 0);

  const isLoadingData = vendorsLoading || productsLoading;
  const loadError = vendorsError || productsError;
  const fieldClass = (hasError: boolean) => cn(inputClass, hasError && inputErrorClass);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={isAmend ? 'Amend Purchase Order' : isEdit ? 'Edit Purchase Order' : 'Create Purchase Order'}
      description={
        isEdit
          ? order?.orderNumber
          : 'New orders start as a draft and are approved and received from the list.'
      }
      size="2xl"
      className="overflow-visible"
    >
      {isLoadingData ? (
        <div className="py-16 text-center text-sm text-gray-500">Loading vendors and products...</div>
      ) : loadError ? (
        <div className="py-12 text-center">
          <p className="text-sm font-medium text-red-600">Could not load vendors or products</p>
          <p className="mt-1 text-sm text-gray-500">{getApiErrorMessage(loadError, 'Please try again.')}</p>
          <button type="button" onClick={onClose} className={cn(secondaryButtonClass, 'mt-4')}>
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit, () => setPlantAsked(true))} noValidate>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            {/* Left: the order itself, in the order it is filled in */}
            <div className="space-y-5 xl:col-span-2">
              <Section step={1} title="Vendor and delivery" description="Who is supplying, and when it is due.">
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">
                  <Field
                    label="Vendor"
                    htmlFor="po-vendor"
                    required
                    error={errors.vendorId?.message}
                    hint={vendorOptions.length === 0 ? 'No active vendors found. Add a vendor first.' : undefined}
                  >
                    <select
                      id="po-vendor"
                      disabled={isAmend}
                      {...register('vendorId', { required: 'Vendor is required' })}
                      className={fieldClass(!!errors.vendorId)}
                    >
                      <option value="">Select a vendor</option>
                      {vendorOptions.map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.name} - {vendor.code}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    label="Expected Date"
                    htmlFor="po-expected-date"
                    required
                    error={errors.expectedDate?.message}
                  >
                    <input
                      id="po-expected-date"
                      type="date"
                      min={isEdit ? undefined : todayInputValue()}
                      {...register('expectedDate', {
                        required: 'Expected date is required',
                        validate: (value) =>
                          isEdit || value >= todayInputValue() || 'Expected date cannot be in the past',
                      })}
                      className={fieldClass(!!errors.expectedDate)}
                    />
                  </Field>

                  {selectedVendor && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md bg-indigo-50 px-3 py-2.5 text-sm sm:col-span-2 sm:grid-cols-4">
                      <div>
                        <dt className="text-xs text-gray-500">Contact</dt>
                        <dd className="truncate font-medium text-gray-900">
                          {selectedVendor.contactPerson || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Phone</dt>
                        <dd className="truncate font-medium text-gray-900">
                          {selectedVendor.contactPhone || selectedVendor.phone || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">GST number</dt>
                        <dd className="truncate font-medium text-gray-900">{selectedVendor.gstNumber || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Payment terms</dt>
                        <dd className="font-medium text-gray-900">
                          {selectedVendor.paymentTerms ? `${selectedVendor.paymentTerms} days` : '—'}
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>
              </Section>

              <Section
                step={2}
                title="Order items"
                description="Products, quantities, prices and tax."
                action={
                  <button
                    type="button"
                    onClick={() => append(newItem(defaultRate))}
                    className="inline-flex shrink-0 items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                }
              >
                <div className="space-y-3 p-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Item {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                          className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900 disabled:text-gray-300 disabled:hover:bg-transparent"
                          title="Remove item"
                          aria-label="Remove item"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-12">
                        <Field
                          label="Product"
                          htmlFor={`po-item-${index}-product`}
                          required
                          className="col-span-2 sm:col-span-5"
                          error={errors.items?.[index]?.productId?.message}
                        >
                          <select
                            id={`po-item-${index}-product`}
                            {...register(`items.${index}.productId`, {
                              required: 'Product is required',
                              onChange: (e) => {
                                const selectedProduct = products.find((p) => p.id === e.target.value);
                                if (selectedProduct) {
                                  // Purchases default to cost price, then fall back to the other prices
                                  const price =
                                    selectedProduct.costPrice ||
                                    selectedProduct.unitPrice ||
                                    selectedProduct.price ||
                                    0;
                                  setValue(`items.${index}.unitPrice`, Number(price));
                                }
                              },
                            })}
                            className={fieldClass(!!errors.items?.[index]?.productId)}
                          >
                            <option value="">Select a product</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.sku}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field
                          label="Quantity"
                          htmlFor={`po-item-${index}-quantity`}
                          required
                          className="sm:col-span-2"
                          error={errors.items?.[index]?.quantity?.message}
                        >
                          <input
                            id={`po-item-${index}-quantity`}
                            type="number"
                            min="0"
                            step="0.0001"
                            {...register(`items.${index}.quantity`, {
                              required: 'Quantity is required',
                              min: { value: 0.0001, message: 'Must be more than zero' },
                              validate: (value) => Math.abs(Math.round(value * 10000) / 10000 - value) < 1e-9 || 'Use at most 4 decimals',
                              valueAsNumber: true,
                            })}
                            className={fieldClass(!!errors.items?.[index]?.quantity)}
                          />
                        </Field>

                        <Field
                          label="Unit Price (₹)"
                          htmlFor={`po-item-${index}-price`}
                          required
                          className="sm:col-span-3"
                          error={errors.items?.[index]?.unitPrice?.message}
                        >
                          <input
                            id={`po-item-${index}-price`}
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`items.${index}.unitPrice`, {
                              required: 'Unit price is required',
                              min: { value: 0, message: 'Cannot be negative' },
                              validate: (value) => Number.isFinite(value) || 'Unit price is required',
                              valueAsNumber: true,
                            })}
                            className={fieldClass(!!errors.items?.[index]?.unitPrice)}
                          />
                        </Field>

                        <Field
                          label="GST (%)"
                          htmlFor={`po-item-${index}-tax`}
                          className="sm:col-span-2"
                          error={errors.items?.[index]?.taxRate?.message}
                        >
                          <select
                            id={`po-item-${index}-tax`}
                            {...register(`items.${index}.taxRate`, { setValueAs: (value) => Number(value) })}
                            className={fieldClass(!!errors.items?.[index]?.taxRate)}
                          >
                            {optionsFor(watchedItems?.[index]?.taxRate).map((rate) => (
                              <option key={rate} value={rate}>
                                {rate}%
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>

                      <p className="mt-3 flex justify-end gap-2 border-t border-gray-200 pt-2 text-sm text-gray-600">
                        Line total (with tax)
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(lineTotals[index]?.total || 0)}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </Section>
            </div>

            {/* Right: running summary and notes */}
            <aside className="xl:col-span-1">
              <div className="space-y-5 xl:sticky xl:top-4">
                <section className="rounded-lg border border-gray-200 bg-white">
                  <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                    Order summary
                  </h4>
                  <div className="space-y-4 p-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Vendor</p>
                      <p className="font-medium text-gray-900">
                        {selectedVendor ? selectedVendor.name : 'Not selected'}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-md bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Items</p>
                        <p className="text-lg font-semibold text-gray-900 tabular-nums">{itemCount}</p>
                      </div>
                      <div className="rounded-md bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Units</p>
                        <p className="text-lg font-semibold text-gray-900 tabular-nums">{totalUnits}</p>
                      </div>
                    </div>
                    <dl>
                      <div className="flex justify-between py-1">
                        <dt className="text-gray-600">Subtotal</dt>
                        <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(subtotal)}</dd>
                      </div>
                      <div className="flex justify-between py-1">
                        <dt className="text-gray-600">Tax</dt>
                        <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(taxTotal)}</dd>
                      </div>
                      <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
                        <dt className="font-semibold text-gray-900">Grand Total</dt>
                        <dd className="text-lg font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(grandTotal)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </section>

                {isAmend && (
                  <section className="rounded-lg border border-amber-300 bg-amber-50">
                    <h4 className="rounded-t-lg border-b border-amber-200 px-4 py-3 text-sm font-semibold text-amber-900">
                      Reason for amendment<span className="ml-0.5 text-red-500">*</span>
                    </h4>
                    <div className="p-4">
                      <label htmlFor="po-amend-reason" className="sr-only">
                        Reason for amendment
                      </label>
                      <textarea
                        id="po-amend-reason"
                        rows={3}
                        maxLength={300}
                        {...register('reason', {
                          validate: (value) => (value ?? '').trim().length >= 3 || 'Say why the order is amended',
                        })}
                        className={inputClass}
                        placeholder="For example: vendor revised the price"
                      />
                      {errors.reason && <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>}
                      <p className="mt-2 text-xs text-amber-800">
                        This order is approved{order?.revision ? ` (revision ${order.revision})` : ''}. The order as it is now is kept with this reason.
                        A quantity cannot go below what has already been received.
                      </p>
                    </div>
                  </section>
                )}

                {!isEdit && plant.scope.required && (
                  <section className="rounded-lg border border-gray-200 bg-white p-4">
                    <PlantField id="po-plant" scope={plant.scope} value={plant.plantId} onChange={plant.setPlantId} showError={plantAsked} />
                  </section>
                )}

                <section className="rounded-lg border border-gray-200 bg-white">
                  <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                    Notes
                  </h4>
                  <div className="p-4">
                    <label htmlFor="po-notes" className="sr-only">
                      Notes
                    </label>
                    <textarea
                      id="po-notes"
                      rows={4}
                      maxLength={255}
                      {...register('notes', {
                        maxLength: { value: 255, message: 'Notes cannot exceed 255 characters' },
                      })}
                      className={inputClass}
                      placeholder="Additional notes or instructions..."
                    />
                    {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>}
                  </div>
                </section>
              </div>
            </aside>
          </div>

          {/* Footer keeps the total and any problems in view next to the buttons */}
          <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm">
              <span className="text-gray-600">Grand Total </span>
              <span className="text-base font-semibold text-gray-900 tabular-nums">
                {formatCurrency(grandTotal)}
              </span>
              {errorCount > 0 && (
                <span className="ml-3 font-medium text-red-600">
                  {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <button type="button" onClick={onClose} disabled={isSaving} className={secondaryButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={primaryButtonClass}>
                {isSaving
                  ? isEdit
                    ? 'Updating...'
                    : 'Creating...'
                  : isEdit
                    ? 'Update Purchase Order'
                    : 'Create Purchase Order'}
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default CreatePurchaseOrder;
